import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  format, isValid, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths,
  setHours, setMinutes, startOfDay, isToday
} from 'date-fns';
import { ru } from 'date-fns/locale/ru';
import { enUS } from 'date-fns/locale/en-US';
import { ro } from 'date-fns/locale/ro';
import Icon from './Icon';
import '../css/custom-datepicker.css';

const LOCALES = { ru, en: enUS, ro };

const pad = (n) => String(n).padStart(2, '0');

/* ─── Time Stepper ─── */
const TimeStepper = ({ value, max, onSelect, label }) => {
  const { t } = useTranslation();
  const touchStartY = useRef(null);
  const touchCurrentY = useRef(null);

  const inc = () => onSelect(value === max ? 0 : (value ?? 0) + 1);
  const dec = () => onSelect(value === 0 ? max : (value ?? 0) - 1);
  
  const handleWheel = (e) => {
    // We can't preventDefault here safely because React uses passive event listeners by default for onWheel.
    // Instead we rely on CSS: overscroll-behavior: contain and touch-action: none on parent.
    if (e.deltaY > 0) dec();
    else if (e.deltaY < 0) inc();
  };

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    // Prevent default scroll behavior while sliding on the stepper
    if (e.cancelable) e.preventDefault();
    
    if (touchStartY.current === null) return;
    touchCurrentY.current = e.touches[0].clientY;
    
    const diff = touchStartY.current - touchCurrentY.current;
    if (Math.abs(diff) > 20) {
      if (diff > 0) inc();
      else dec();
      touchStartY.current = touchCurrentY.current;
    }
  };

  const handleTouchEnd = () => {
    touchStartY.current = null;
    touchCurrentY.current = null;
  };

  return (
    <div className="cdp-stepper-col">
      <button type="button" className="cdp-stepper-btn" onClick={inc} title={t('common.datePicker.increase')}>
        <Icon name="chevron-up" size={24} />
      </button>
      <div 
        className="cdp-stepper-val" 
        onWheel={handleWheel}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {value !== null && value !== undefined ? pad(value) : '00'}
      </div>
      <button type="button" className="cdp-stepper-btn" onClick={dec} title={t('common.datePicker.decrease')}>
        <Icon name="chevron-down" size={24} />
      </button>
      <div className="cdp-stepper-label">{label}</div>
    </div>
  );
};

/* ─── Main DateTimePicker ─── */
const CustomDatePicker = ({ selected, onChange, placeholderText, showTimeSelect = true }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('date'); // 'date' | 'time'
  const [slideDir, setSlideDir] = useState(1);
  const wrapperRef = useRef(null);
  
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hasError, setHasError] = useState(false);

  const activeLang = (i18n.language && i18n.language.includes('ru')) ? 'ru' :
                     (i18n.language && i18n.language.includes('ro')) ? 'ro' : 'en';
  const currentLocale = LOCALES[activeLang] || enUS;

  const [currentMonth, setCurrentMonth] = useState(startOfMonth(selected || new Date()));

  // Sync text input with selected date when not focused
  useEffect(() => {
    if (!isFocused) {
      if (selected && isValid(selected)) {
        setInputValue(format(selected, showTimeSelect ? 'dd.MM.yyyy HH:mm' : 'dd.MM.yyyy'));
      } else {
        setInputValue('');
      }
      setHasError(false);
    }
  }, [selected, showTimeSelect, isFocused]);

  // Sync month view when selected changes AND popover is closed
  useEffect(() => {
    if (selected && isValid(selected) && !isOpen) {
      setCurrentMonth(startOfMonth(selected));
    }
  }, [selected, isOpen]);

  // Click outside -> close popover and process input
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInputChange = (e) => {
    let raw = e.target.value;
    
    // Remove non-digit characters if user is typing
    // but allow backspace and natural formatting
    
    // Simple mask logic for DD.MM.YYYY HH:mm
    if (e.nativeEvent.inputType !== 'deleteContentBackward') {
      const digits = raw.replace(/\D/g, '');
      let formatted = '';
      
      if (digits.length > 0) formatted += digits.substring(0, 2);
      if (digits.length >= 2) formatted += '.' + digits.substring(2, 4);
      if (digits.length >= 4) formatted += '.' + digits.substring(4, 8);
      
      if (showTimeSelect) {
        if (digits.length >= 8) formatted += ' ' + digits.substring(8, 10);
        if (digits.length >= 10) formatted += ':' + digits.substring(10, 12);
      }
      
      setInputValue(formatted);
    } else {
      setInputValue(raw);
    }
    
    setHasError(false);
  };

  const processInput = () => {
    const val = inputValue.trim();
    if (!val) {
      onChange(null);
      setHasError(false);
      return;
    }

    // Parse logic: DD.MM.YYYY or DD.MM.YYYY HH:mm
    // separators can be dot, dash, slash, space
    const match = val.match(/^(\d{1,2})[\.\-\/]?(\d{1,2})[\.\-\/]?(\d{2,4})(?:\s+(\d{1,2})[:\.\-]?(\d{1,2}))?$/);
    
    if (match) {
      let [_, d, m, y, h, min] = match;
      d = parseInt(d, 10);
      m = parseInt(m, 10);
      y = parseInt(y, 10);
      
      // Auto-fix partial year
      if (y < 100) {
        y += 2000;
      }
      
      if (m < 1 || m > 12 || d < 1 || d > 31) {
        setHasError(true);
        return;
      }

      const parsedDate = new Date(y, m - 1, d);
      if (parsedDate.getMonth() !== m - 1) { // e.g. Feb 31 -> Mar 3
        setHasError(true);
        return;
      }

      if (h !== undefined && min !== undefined) {
        h = parseInt(h, 10);
        min = parseInt(min, 10);
        if (h < 0 || h > 23 || min < 0 || min > 59) {
          setHasError(true);
          return;
        }
        parsedDate.setHours(h, min, 0, 0);
        onChange(parsedDate);
        setHasError(false);
      } else {
        // Only date entered
        // Keep existing time if selected
        if (selected && isValid(selected)) {
          parsedDate.setHours(selected.getHours(), selected.getMinutes(), 0, 0);
        } else {
          parsedDate.setHours(0, 0, 0, 0);
        }
        
        onChange(parsedDate);
        setHasError(false);
        
        // Auto-redirect to time tab if time is required
        if (showTimeSelect) {
          setIsOpen(true);
          setActiveTab('time');
        }
      }
      setCurrentMonth(startOfMonth(parsedDate));
    } else {
      setHasError(true);
    }
  };

  const handleInputKeyDown = (e) => {
    // Only allow numbers, control keys, and our format characters
    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Delete', 'Escape'];
    if (!/^\d$/.test(e.key) && !allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      processInput();
      if (!hasError && !(showTimeSelect && inputValue.indexOf(':') === -1)) {
        setIsOpen(false);
      }
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    processInput();
  };

  const handleInputFocus = () => {
    setIsFocused(true);
  };

  const togglePopover = () => {
    if (!isOpen) {
      if (!selected) setActiveTab('date');
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  const clearDate = (e) => {
    e.stopPropagation();
    onChange(null);
    setInputValue('');
    setHasError(false);
    setIsOpen(false);
  };

  const handleDayClick = (clickedDay) => {
    const base = selected && isValid(selected) ? selected : startOfDay(new Date());
    const h = base.getHours();
    const mi = base.getMinutes();
    let newDate = setHours(setMinutes(clickedDay, mi), h);
    
    onChange(newDate);
    
    if (!isSameMonth(clickedDay, currentMonth)) {
      setSlideDir(clickedDay > currentMonth ? 1 : -1);
      setCurrentMonth(startOfMonth(clickedDay));
    }
    
    if (showTimeSelect) {
      setActiveTab('time');
    } else {
      setIsOpen(false);
    }
  };

  const handleHourSelect = (h) => {
    const base = selected && isValid(selected) ? selected : startOfDay(new Date());
    onChange(setHours(base, h));
  };

  const handleMinuteSelect = (m) => {
    const base = selected && isValid(selected) ? selected : startOfDay(new Date());
    onChange(setMinutes(base, m));
  };

  const handleNow = () => {
    const now = new Date();
    onChange(now);
    setCurrentMonth(startOfMonth(now));
    if (showTimeSelect) setActiveTab('time');
  };

  const changeMonth = (offset) => {
    setSlideDir(offset);
    setCurrentMonth(prev => offset > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
  };

  /* ── extract segments for time steppers ── */
  const hour  = selected && isValid(selected) ? selected.getHours()   : 0;
  const minute = selected && isValid(selected) ? selected.getMinutes() : 0;

  /* ── calendar grid ── */
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const s = startOfWeek(monthStart, { weekStartsOn: 1 });
    const e = endOfWeek(monthEnd, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: s, end: e });
  }, [currentMonth]);

  const weekDayLabels = useMemo(() => {
    const map = { ru: ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'], ro: ['Lu','Ma','Mi','Jo','Vi','Sâ','Du'], en: ['Mo','Tu','We','Th','Fr','Sa','Su'] };
    return map[activeLang] || map.en;
  }, [activeLang]);

  const nowLabel = t('common.datePicker.now');
  const timeLabel = t('common.datePicker.time');
  const dateLabel = t('common.datePicker.date');

  return (
    <div className="cdp-wrapper" ref={wrapperRef}>
      {/* ─── Input Area ─── */}
      <div className={`cdp-input-bar ${isOpen ? 'open' : ''} ${hasError ? 'error' : ''}`}>
        <input
          type="text"
          className="cdp-smart-input"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onKeyDown={handleInputKeyDown}
          placeholder={placeholderText || (showTimeSelect ? t('common.datePicker.placeholderDateTime') : t('common.datePicker.placeholderDate'))}
          onClick={() => { if(!isOpen) setIsOpen(true); }}
        />

        <div className="cdp-input-actions">
          {selected || inputValue ? (
            <button type="button" className="cdp-icon-btn cdp-clear-btn" onClick={clearDate} title={t('common.datePicker.clear')}>
              <Icon name="close" size={14} />
            </button>
          ) : null}
          <button
            type="button"
            className={`cdp-icon-btn ${isOpen ? 'active' : ''}`}
            onClick={togglePopover}
          >
            <Icon name="calendar" size={17} />
          </button>
        </div>
      </div>

      {/* ─── Popover ─── */}
      {/* Портал в <body>: календарь центрируется по вьюпорту и не уходит вниз
          за пределы экрана внутри узких/скроллящихся меню фильтров. Клик по
          подложке закрывает только календарь и возвращает к фильтрам.
          stopPropagation на mousedown не даёт родительскому меню (его слушатель
          «клик вне» висит на document) закрыться, ведь портал вне его DOM. */}
      {createPortal(
        <AnimatePresence mode="wait">
        {isOpen && (
          <motion.div
            className="cdp-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setIsOpen(false)}
          >
          <motion.div
            className="cdp-popover"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{
              duration: 0.18, ease: 'easeOut',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Поле ручного ввода внутри карточки — подложка перекрывает
                внешний инпут, поэтому дублируем его здесь, чтобы дату можно
                было и написать вручную, и выбрать в календаре. */}
            <div className={`cdp-input-bar cdp-popover-input-bar ${hasError ? 'error' : ''}`}>
              <input
                type="text"
                className="cdp-smart-input"
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onFocus={handleInputFocus}
                onKeyDown={handleInputKeyDown}
                placeholder={placeholderText || (showTimeSelect ? t('common.datePicker.placeholderDateTime') : t('common.datePicker.placeholderDate'))}
              />
              {(selected || inputValue) ? (
                <button type="button" className="cdp-icon-btn cdp-clear-btn" onClick={clearDate} title={t('common.datePicker.clear')}>
                  <Icon name="close" size={14} />
                </button>
              ) : null}
            </div>

            {showTimeSelect && (
              <div className="cdp-tabs">
                <button 
                  type="button"
                  className={`cdp-tab ${activeTab === 'date' ? 'active' : ''}`}
                  onClick={() => setActiveTab('date')}
                >
                  <Icon name="calendar" size={14} />
                  {dateLabel}
                </button>
                <button 
                  type="button"
                  className={`cdp-tab ${activeTab === 'time' ? 'active' : ''}`}
                  onClick={() => setActiveTab('time')}
                >
                  <Icon name="clock" size={14} />
                  {timeLabel}
                </button>
              </div>
            )}

            <div className="cdp-body" style={{ position: 'relative' }}>
              <AnimatePresence mode="wait">
                {/* Calendar View */}
              {(!showTimeSelect || activeTab === 'date') && (
                <motion.div 
                  className="cdp-calendar"
                  key="date"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="cdp-cal-header">
                    <button type="button" className="cdp-nav-btn" onClick={() => changeMonth(-1)}>
                      <Icon name="chevron-left" size={16} />
                    </button>
                    <div className="cdp-month-label">
                      <span className="cdp-month-name">{format(currentMonth, 'LLLL', { locale: currentLocale })}</span>
                      <span className="cdp-month-year">{format(currentMonth, 'yyyy')}</span>
                    </div>
                    <button type="button" className="cdp-nav-btn" onClick={() => changeMonth(1)}>
                      <Icon name="chevron-right" size={16} />
                    </button>
                  </div>

                  <div className="cdp-weekdays">
                    {weekDayLabels.map(d => <span key={d}>{d}</span>)}
                  </div>

                  <div className="cdp-grid-wrapper">
                    <AnimatePresence mode="popLayout" initial={false} custom={slideDir}>
                      <motion.div
                        key={currentMonth.toISOString()}
                        className="cdp-grid"
                        custom={slideDir}
                        variants={{
                          enter: () => ({ opacity: 0 }),
                          center: { opacity: 1 },
                          exit: () => ({ opacity: 0 })
                        }}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ duration: 0.18, ease: 'easeOut' }}
                      >
                        {calendarDays.map(d => {
                          const isSel = selected && isSameDay(d, selected);
                          const isCur = isSameMonth(d, currentMonth);
                          const isTod = isToday(d);
                          return (
                            <button
                              key={d.toISOString()}
                              type="button"
                              onClick={() => handleDayClick(d)}
                              className={`cdp-day ${!isCur ? 'dim' : ''} ${isSel ? 'sel' : ''} ${isTod && !isSel ? 'today' : ''}`}
                            >
                              {format(d, 'd')}
                            </button>
                          );
                        })}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                  </motion.div>
              )}

              {/* Time View */}
              {showTimeSelect && activeTab === 'time' && (
                <motion.div 
                  className="cdp-time-view"
                  key="time"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                >
                  <div className="cdp-stepper-container">
                    <TimeStepper value={hour} max={23} onSelect={handleHourSelect} label={t('common.datePicker.hourLetter')} />
                    <div className="cdp-stepper-divider">:</div>
                    <TimeStepper value={minute} max={59} onSelect={handleMinuteSelect} label={t('common.datePicker.minuteLetter')} />
                  </div>
                </motion.div>
              )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="cdp-footer">
              <button type="button" className="cdp-now-btn" onClick={handleNow}>
                <Icon name="clock" size={13} />
                {nowLabel}
              </button>
              <button type="button" className="cdp-done-btn" onClick={() => setIsOpen(false)}>
                {t('common.datePicker.ok')}
              </button>
            </div>
          </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
      )}
    </div>
  );
};

export default CustomDatePicker;
